import {
  Modal as ChakraModal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalCloseButton,
  ModalBody,
  Button,
  Box,
} from '@chakra-ui/react';

export interface ModalProps {
  title: string | JSX.Element;
  content: JSX.Element;
  isOpen: boolean;
  onClose?: () => void;
  showCloseButton?: boolean;
  showSubmitButton?: boolean;
  submitText?: string;
  closeText?: string;
  onSubmit?: () => void;
  scrollEnabled?: boolean;

  /** New: switch modal chrome/layout */
  variant?: 'standard' | 'takeover';
}

export default function Modal({
  title,
  content,
  isOpen,
  showSubmitButton = true,
  showCloseButton = true,
  closeText = 'Close',
  submitText = 'Submit',
  onClose = () => { },
  onSubmit = () => { },
  scrollEnabled = true,
  variant = 'standard',
}: ModalProps) {
  // Standard chrome buttons
  function renderCloseButton() {
    if (showCloseButton) {
      return (
        <Button variant="ghost" mr={3} onClick={onClose}>
          {closeText}
        </Button>
      );
    }
    return null;
  }

  function renderSubmitButton() {
    if (showSubmitButton) {
      return (
        <Button colorScheme="blue" onClick={onSubmit}>
          {submitText}
        </Button>
      );
    }
    return null;
  }

  // Full-takeover: full screen, no padding, no header/footer unless you still want the X button.
  if (variant === 'takeover') {
    return (
      <ChakraModal
        isOpen={isOpen}
        onClose={onClose}
        blockScrollOnMount={false}
        returnFocusOnClose={false}
      >
        <ModalOverlay />
        <ModalContent
          marginTop={'auto'} marginBottom={{ base: 0, md: 'auto' }}
          borderRadius={0}
          boxShadow="none"
          bg="transparent" // let the child component own its surface
          // allow the child to scroll internally if it's taller than viewport
          overflow={scrollEnabled ? 'auto' : 'hidden'}
        >
          {/* Optional close button in the top-right */}
          {showCloseButton ? <ModalCloseButton /> : null}

          {/* Render content directly, no ModalBody padding */}
          {content}
        </ModalContent>
      </ChakraModal>
    );
  }

  // Default (existing) modal chrome
  return (
    <ChakraModal blockScrollOnMount={false} isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent marginTop={'auto'} marginBottom={{ base: 0, md: 'auto' }}>
        <ModalHeader textAlign="center">{title}</ModalHeader>
        <ModalCloseButton />
        <ModalBody
          maxHeight={{ base: '400px', md: '600px' }}
          overflow={scrollEnabled ? 'scroll' : 'hidden'}
        >
          {content}
        </ModalBody>
        <ModalFooter>
          {renderCloseButton()}
          {renderSubmitButton()}
        </ModalFooter>
      </ModalContent>
    </ChakraModal>
  );
}
